import { ref, onUnmounted, inject, readonly, computed, onMounted } from 'vue';

/**
 * Composable for managing intervals with Cordova pause/resume support
 * @param {Object} options - Configuration options
 * @param {boolean} options.pauseOnCordovaPause - Whether to pause intervals when Cordova app is paused
 * @param {string} options.loggerTag - Tag for logging
 */
export function useInterval(options = {}) {
  const {
    pauseOnCordovaPause = true,
    loggerTag = 'useInterval'
  } = options;

  const logger = inject('logger');
  const intervals = ref(new Map());
  const isPaused = ref(false);
  const cordovaInitialized = ref(false);

  /**
   * Create and start an interval
   * @param {string} key - Unique identifier for the interval
   * @param {Function} callback - Function to execute
   * @param {number} delay - Delay in milliseconds
   * @param {boolean} immediate - Whether to execute immediately
   */
  const setInterval = (key, callback, delay, immediate = false) => {
    clearInterval(key);
    
    if (immediate && !isPaused.value) {
      callback();
    }

    if (!isPaused.value) {
      const intervalId = window.setInterval(callback, delay);
      intervals.value.set(key, {
        id: intervalId,
        callback,
        delay,
        immediate
      });
      
      logger?.info(loggerTag, `Interval '${key}' started with delay ${delay}ms`);
    } else {
      // Store interval config for later restart
      intervals.value.set(key, {
        id: null,
        callback,
        delay,
        immediate
      });
      
      logger?.info(loggerTag, `Interval '${key}' stored (paused)`);
    }
  };

  /**
   * Clear a specific interval
   * @param {string} key - Interval identifier
   */
  const clearInterval = (key) => {
    const interval = intervals.value.get(key);
    if (interval?.id) {
      window.clearInterval(interval.id);
      logger?.info(loggerTag, `Interval '${key}' cleared`);
    }
    intervals.value.delete(key);
  };

  /**
   * Clear all intervals
   */
  const clearAllIntervals = () => {
    intervals.value.forEach((interval, key) => {
      if (interval.id) {
        window.clearInterval(interval.id);
      }
    });
    intervals.value.clear();
    logger?.info(loggerTag, 'All intervals cleared');
  };

  /**
   * Pause all intervals
   */
  const pauseAllIntervals = () => {
    if (isPaused.value) return;
    
    isPaused.value = true;
    intervals.value.forEach((interval, key) => {
      if (interval.id) {
        window.clearInterval(interval.id);
        interval.id = null;
      }
    });
    logger?.info(loggerTag, 'All intervals paused');
  };

  /**
   * Resume all intervals
   */
  const resumeAllIntervals = () => {
    if (!isPaused.value) return;
    
    isPaused.value = false;
    intervals.value.forEach((interval, key) => {
      if (!interval.id) {
        if (interval.immediate) {
          interval.callback();
        }
        interval.id = window.setInterval(interval.callback, interval.delay);
      }
    });
    logger?.info(loggerTag, 'All intervals resumed');
  };

  /**
   * Initialize Cordova event listeners
   */
  const initializeCordova = () => {
    if (cordovaInitialized.value || !pauseOnCordovaPause) return;

    document.addEventListener('pause', () => {
      logger?.info(loggerTag, 'Cordova pause event - pausing intervals');
      pauseAllIntervals();
    });

    document.addEventListener('resume', () => {
      logger?.info(loggerTag, 'Cordova resume event - resuming intervals');
      resumeAllIntervals();
    });

    cordovaInitialized.value = true;
    logger?.info(loggerTag, 'Cordova event listeners initialized');
  };

  onMounted(() => {
    // Initialize Cordova event listeners if needed
    if (typeof window.cordova !== 'undefined') {
      initializeCordova();
    } else {
      logger?.warn(loggerTag, 'Cordova not detected, skipping initialization');
    }
  });

  // Cleanup on unmount
  onUnmounted(() => {
    clearAllIntervals();
  });

  return {
    setInterval,
    clearInterval,
    clearAllIntervals,
    pauseAllIntervals,
    resumeAllIntervals,
    initializeCordova,
    isPaused: readonly(isPaused),
    intervalCount: computed(() => intervals.value.size)
  };
}
